import { useState, useEffect, useCallback } from 'react';
import { useBybitWebSocket } from './useBybitWebSocket';
import { bybitService } from '../services/bybitService';
import { MarketData } from './useDatabase';

export const useBybitData = (symbols: string[] = []) => {
  const [marketData, setMarketData] = useState<MarketData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // Use WebSocket for the primary symbol (first in the list)
  const primarySymbol = symbols[0] || 'BTCUSDT';
  const { ticker, tickers, isConnected } = useBybitWebSocket(symbols);

  // Fetch market data for all symbols with better error handling
  const fetchMarketData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all tickers with fallback handling
      const tickers = await bybitService.getAllTickers();
      
      // Filter tickers by symbols if provided, otherwise use all USDT pairs
      const filteredTickers = symbols.length > 0
        ? tickers.filter(ticker => symbols.includes(ticker.symbol))
        : tickers.filter(ticker => ticker.symbol.endsWith('USDT'));
      
      // Transform to MarketData format
      const formattedData: MarketData[] = filteredTickers.map(tickerData => 
        bybitService.convertTickerToMarketData(tickerData)
      );
      
      // If we have real-time ticker data from WebSocket, update the price for that symbol
      if (ticker) {
        const index = formattedData.findIndex(data => data.symbol === ticker.symbol);
        if (index !== -1) {
          formattedData[index] = {
            ...formattedData[index],
            price: ticker.price,
            timestamp: ticker.timestamp.toISOString()
          };
        }
      }
      
      setMarketData(formattedData);
      setLastUpdated(new Date());
      
      return formattedData;
    } catch (err) {
      console.warn('Error fetching Bybit market data, continuing with cached data:', err);
      setError('Using cached market data due to connectivity issues');
      // Return current market data instead of empty array to preserve last known good data
      return marketData;
    } finally {
      setLoading(false);
    }
  }, [symbols, ticker, marketData]);

  // Update market data when ticker changes
  useEffect(() => {
    if (ticker) {
      setMarketData(prevData => {
        const newData = [...prevData];
        const index = newData.findIndex(data => data.symbol === ticker.symbol);
        
        if (index !== -1) {
          newData[index] = {
            ...newData[index],
            price: ticker.price,
            timestamp: ticker.timestamp.toISOString()
          };
        }
        
        return newData;
      });
      
      setLastUpdated(new Date());
    }
  }, [ticker]);

  // Update market data when any ticker in tickers object changes
  useEffect(() => {
    if (tickers && Object.keys(tickers).length > 0) {
      setMarketData(prevData => {
        const newData = [...prevData];
        
        // Update each market data entry with the corresponding ticker data
        Object.entries(tickers).forEach(([symbol, tickerData]) => {
          if (tickerData && typeof tickerData.price === 'number' && tickerData.price > 0) {
            const index = newData.findIndex(data => data.symbol === symbol);
            
            if (index !== -1) {
              // Update existing entry
              newData[index] = {
                ...newData[index],
                price: tickerData.price,
                change_24h: tickerData.priceChangePercent || newData[index].change_24h,
                timestamp: tickerData.timestamp.toISOString()
              };
            } else {
              // Add new entry if it doesn't exist
              newData.push({
                symbol: symbol,
                price: tickerData.price,
                volume_24h: tickerData.volume || 0,
                change_24h: tickerData.priceChangePercent || 0,
                timestamp: tickerData.timestamp.toISOString()
              });
            }
          }
        });
        
        return newData;
      });
      
      setLastUpdated(new Date());
    }
  }, [tickers]);

  // Fetch all market data once on mount and then every 15 seconds (increased from 10)
  useEffect(() => {
    // Fetch market data initially
    fetchMarketData();
    
    // Set up interval to fetch market data every 15 seconds (increased for better stability)
    const interval = setInterval(() => {
      fetchMarketData();
    }, 15000);
    
    // Clean up interval on unmount
    return () => clearInterval(interval);
  }, [fetchMarketData]);

  return {
    marketData,
    loading: loading && !isConnected,
    error,
    lastUpdated,
    refetch: fetchMarketData
  };
};